<template>
  <div class="manage-identities-page">
    <h1>Manage identities</h1>
    <PaginatedListView
      :fetchPage="fetchPage"
      :getRowKey="(n) => n.subject!"
      :sortOptions="sortOptions"
      searchPlaceholder="Search for a name, address, or subject"
      :showSearch="true"
      :pageSize="25">
      <template #header>
        <div class="grid-cell">Name</div>
        <div class="grid-cell">Address</div>
        <div class="grid-cell">Status</div>
        <div class="grid-cell">Subject</div>
        <div class="grid-cell">Signed up</div>
        <div class="grid-cell"></div>
        <div class="grid-cell">Last sign in</div>
        <div class="grid-cell"></div>
      </template>
      <template #row="{ item: n }">
        <div class="grid-cell name">{{ getDisplayName(n) }}</div>
        <div class="grid-cell addr" :class="{ verified: n.addresses?.at(0)?.verifiedDate }">
          {{ n.addresses?.at(0)?.address }}
        </div>
        <div class="grid-cell status" :class="statusClass(n)">
          <span class="badge badge-xs">{{ statusText(n) }}</span>
        </div>
        <div class="grid-cell mono dim">{{ n.subject }}</div>
        <div class="grid-cell date">{{ toDateText(n.createdDate) }}</div>
        <div class="grid-cell num dim">{{ toAgeText(n.createdDate) }}</div>
        <div class="grid-cell date">{{ toDateText(n.lastSignInDate) }}</div>
        <div class="grid-cell num dim">{{ toAgeText(n.lastSignInDate) }}</div>
      </template>
    </PaginatedListView>
  </div>
</template>

<style lang="scss" scoped>
.manage-identities-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

:deep(.results) {
  grid-template-columns: repeat(4, auto) 60px repeat(4, auto);
}

.addr:not(.verified) {
  color: #888;
}

.mono {
  font-family: monospace;
}

.status {
  span {
    padding: 1px 4px;
    color: #333;
  }

  &.ok span {
    background-color: rgb(128, 238, 89);
  }

  &.blocked span {
    background-color: rgb(253, 50, 50);
    color: white;
  }

  &.stalled span {
    background-color: #faec56;
  }
}

.dim {
  color: #aaa;
}
</style>

<script lang="ts" setup>
import { ref } from 'vue'
import { useIdentityApi, UserIdentity, UserIdentitySort } from '@/auth/IdentityApi'
import { DateTime } from 'luxon'
import { DurationUtil } from '@/util/LuxonUtil'
import { isDefined } from '@/util/TypeScriptUtil'
import PaginatedListView from '@/util/PaginatedListView.vue'

const sortOptions = [
  { value: UserIdentitySort.CreatedDate, label: 'Newest' },
  { value: UserIdentitySort.FamilyName, label: 'Last name' },
  { value: UserIdentitySort.GivenName, label: 'First name' },
]

const sortRef = ref(UserIdentitySort.CreatedDate)

enum Status {
  OK = 'OK',
  Stalled = 'Stalled',
  Blocked = 'Blocked',
}

const identityApi = useIdentityApi()

async function fetchPage(options: { search?: string; sort: UserIdentitySort; pageSize: number; next?: string }) {
  sortRef.value = options.sort
  return await identityApi.searchAsync({ text: options.search, sort: options.sort, pageSize: options.pageSize, next: options.next })
}

function getDisplayName(n: UserIdentity) {
  return sortOptions.find(s => s.value === UserIdentitySort.FamilyName) && sortRef.value === UserIdentitySort.FamilyName
    ? [n.familyName, n.givenName].filter(isDefined).join(', ')
    : [n.givenName, n.familyName].filter(isDefined).join(' ')
}

function getStatus(n: UserIdentity): Status {
  return n.blocked ? Status.Blocked : !n.lastSignInDate ? Status.Stalled : Status.OK
}

function statusText(n: UserIdentity) {
  return getStatus(n)
}

function statusClass(n: UserIdentity) {
  return getStatus(n).toLowerCase()
}

function toDateText(dt?: DateTime) {
  return dt?.toLocal().toFormat('yyyy-MM-dd')
}

function toAgeText(dt?: DateTime) {
  return dt ? DurationUtil.toAge(dt.diffNow().negate()) : ''
}
</script>
